import {
  productService,
  stockQuantityService,
  warehouseService,
  contractLineService,
  locationService,
  categoryService,
  contractService
} from 'services/api';
import { loadingActions } from 'features';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import WarehouseForm from '../WarehouseForm';
import { useEffect, useRef, useState } from 'react';
import {
  IContractLine,
  ILocation,
  IOption,
  IOptionDescription,
  IStatusOption,
  IWarehouse
} from 'interfaces';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import EntityHistory from 'components/widgets/EntityHistory';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import { ThresholdsColumns } from 'components/common/enhanced/common-headers/thresholds';
import { StockQuantitiesColumns } from 'components/common/enhanced/common-headers';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { productWarehouseService } from 'services/api/ProductWarehouseService';
import { IsSuperAdmin, IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import toast from 'features/toast';
import { DocumentTableCardComponent } from 'components/common/documentLM/DocumentTableCardComponent';
import ProductWarehouseForm from '../ProductWarehouseForm';
import { labelThresholdService } from 'services/api/LabelThresholdService';
import TurnedInNotIcon from '@mui/icons-material/TurnedInNot';
import LabelThresholdForm from '../LabelThresholdForm';
import ConfirmationButtonDialog from 'components/helpers/ConfirmationButtonDelete';
import { isTechnicianCoordinator } from 'components/helpers/IsTechnicianCoordinator';
import { isTechnician } from 'components/helpers/IsTechnician';
import { ProductWarehouseStatus } from '../../../../constants';
import { LocationColumns } from 'components/common/enhanced/common-headers/locations';
import { ContractLinesWarehouseColumns } from 'components/common/enhanced/common-headers/contract-lines-warehouse';
import AddIcon from '@mui/icons-material/Add';
import LocationForm from '../../Locations/LocationForm';
import WarehouseContractLineForm from '../WarehouseContractLineForm';
import { ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN } from '../../../../constants';

const possibleStatusOptions: IStatusOption[] = Object.entries(ProductWarehouseStatus).map(
  ([display_name, key]) => {
    return {
      value: Number(key),
      display_name: String(display_name)
    };
  }
);

export function EditWarehouse() {
  const translationPrefix = 'pages.warehouse.edit';
  const { warehouseId } = useParams();
  const thresholdsRefresh = useRef(null);

  const initLocation: ILocation = {
    id: null,
    code: '',
    description: '',
    warehouse: warehouseId
  };

  const isDisabledEdit = isTechnicianCoordinator() || isTechnician();

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const groups = useAppSelector((state) => state.auth.groups);
  const hideTenantAdminSections =
    groups.includes(ROLE_TENANT_ADMIN) && !groups.includes(ROLE_SUPER_ADMIN);

  const [warehouse, setWarehouse] = useState<IWarehouse>();
  const [categories, setCategories] = useState<IOptionDescription[]>([]);
  const [products, setProducts] = useState<IOption[]>([]);
  const [labelOptions, setLabelOptions] = useState<IOptionDescription[]>([]);
  const [labelCodes, setLabelCodes] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<IStatusOption[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openContractLineDialog, setOpenContractLineDialog] = useState(false);
  const [openContractLineInsertDialog, setOpenContractLineInsertDialog] = useState(false);
  const [contractLines, setContractLines] = useState([]);
  const [contracts, setContracts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);

  useEffect(() => {
    if (warehouseId) {
      warehouseService.get(+warehouseId).then(setWarehouse);
      productService.getAllBaseInformation().then(setProducts);
      categoryService.getAllBaseInformation().then(setCategories);
      contractService.getAllBaseInformation().then(setContracts);
    }
    productWarehouseService.options().then((res) => {
      setStatusOptions(
        res.actions.POST.status.choices.map((item) => {
          return {
            ...item,
            translated_display_name: t(`entities.product.status_options.${item.display_name}`)
          };
        })
      );
    });
    labelThresholdService.getAllBaseInformation().then(setLabelOptions);
  }, []);

  const updateWarehouse = async (values) => {
    dispatch(loadingActions.startLoading());
    warehouseService.update(warehouse.id, values).then((res) => {
      enqueueSnackbar('Magazzino modificato con successo');
    });
    dispatch(loadingActions.stopLoading());
  };

  const handleDeleteContractLine = async (id: number) => {
    contractLineService.delete(id).then(() => {
      if (selectedProduct) {
        onManagePrices(selectedProduct);
      }
    });
  };
  const handleThresholdsBulkDelete = (id: number) => {
    productWarehouseService.bulkDeleteWarehouse(+warehouseId).then(() => {
      thresholdsRefresh.current();
    });
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleCloseContractDialog = () => {
    setOpenContractLineDialog(false);
  };

  const handleCloseContractInsertDialog = () => {
    setOpenContractLineInsertDialog(false);
  };

  const addThreshold = (values) => {
    values['warehouse'] = +warehouseId;

    const label = labelOptions.find((label) => label?.id === values?.label)?.code;
    if (label) values['label'] = label;
    productWarehouseService.add(values).then(() => {
      toast.success(t(`${translationPrefix}.threshold-created`));
      thresholdsRefresh.current();
    });
    handleClose();
  };

  const createLocation = async (values) => {
    const payload = {
      ...values,
      warehouse: +warehouseId
    };
    locationService.add(payload).then((res) => {
      enqueueSnackbar('Piazza aggiunta con successo');
    });
    setOpenLocationDialog(false);
  };

  // --------------------------------------------- Create Label Threshold ---------------------------------------------
  const [openLabelDialog, setOpenLabelDialog] = useState(false);

  /**
   * Opens the label dialog.
   */
  const handleOpenLabelDialog = () => {
    setOpenLabelDialog(true);
  };

  /**
   * Closes the label dialog.
   */
  const handleCloseLabelDialog = () => {
    setOpenLabelDialog(false);
  };

  const handleLocationDialog = () => {
    setOpenLocationDialog(true);
  };

  const createContractLine = async (values) => {
    productService.getAllBaseInformation({ code: selectedProduct }).then((product) => {
      const productId = product[0].id;
      const lineToAdd: IContractLine = {
        id: Math.random() * 1000000,
        product: productId,
        unit_price: values.unit_price.replace(',', '.'),
        initial_available_units: values.initial_available_units,
        available_units: values.initial_available_units,
        used_units: 0,
        initial_discounted_units: values.initial_discounted_units,
        available_discounted_units: values.initial_discounted_units,
        used_discounted_units: 0,
        warehouse: +warehouseId,
        iva: values.iva
      };
      contractService.assignProducts([lineToAdd], values.contract_header).then(() => {
        enqueueSnackbar('Riga contratto aggiunto con successo');
        toast.success(t(`${translationPrefix}.contractline-created`));

        // ✅ Refresh della tabella dopo inserimento
        if (selectedProduct) {
          onManagePrices(selectedProduct);
        }

        setOpenContractLineInsertDialog(false);
      });
    });
  };

  /**
   * Creates a label threshold.
   *
   * @param {Object} values - The values of the label threshold.
   * @return {Promise<void>} A promise that resolves when the label threshold is created.
   */
  const createLabelThreshold = async (values) => {
    // Add the label threshold
    await labelThresholdService.add(values).then(() => {
      // // Refresh the label options
      // labelThresholdService.getAllPaginated().then((res) => {
      //   if (res) {
      //     setLabelList(res.results.map((label) => label.code));
      //   }
      // });

      labelThresholdService.getAllBaseInformation().then((res) => {
        setLabelCodes(res.map((opt) => opt.code));
      });

      // Show a success toast message
      toast.success(t(`${translationPrefix}.label.created`));

      // Refresh the thresholds
      thresholdsRefresh.current();
    });

    // Close the dialog
    handleCloseLabelDialog();
  };

  const onManagePrices = (product) => {
    contractLineService
      .getAllBaseInformation({
        product: product,
        warehouse_id: warehouseId
      })
      .then((res) => {
        setContractLines(res);
      });
    setSelectedProduct(product);
    setOpenContractLineDialog(true);
  };

  const isAdmin = IsSuperAdminGuard();
  const columns: GridColDef[] = StockQuantitiesColumns(isAdmin);
  const locationsColumns: GridColDef[] = LocationColumns();
  const thresholdColumns = ThresholdsColumns(
    true,
    onManagePrices,
    thresholdsRefresh,
    statusOptions,
    categories
  );
  const contractLineColumns: GridColDef[] = ContractLinesWarehouseColumns(handleDeleteContractLine);

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={warehouse?.code}>
      {warehouse && (
        <div className="flex flex-wrap">
          <WarehouseForm
            warehouse={warehouse}
            onSubmit={updateWarehouse}
            disableEdit={isDisabledEdit}
          />
          <br />
          <div className="w-full py-4">
            <Card variant="outlined">
              <DocumentTableCardComponent
                originalService={'warehouse'}
                objectId={warehouse.id}
                dictParams={{ warehouse: warehouseId }}
                folder={'menu.warehouses/' + warehouse.code}
                folder_description=""
                disableEdit={isDisabledEdit}
              />
            </Card>
          </div>
          {/* Quantities */}
          <div className="w-full py-2">
            <Card variant="outlined">
              <CardContent>
                <b className="text-primary-600">{t('menu.stock')}</b>
                <br />
                <EnhancedDataGrid
                  service={stockQuantityService}
                  getMethod={stockQuantityService.getStockQuantityWithLocation}
                  columns={columns}
                  outerParams={{
                    warehouse__id: `${warehouse.id}`
                  }}
                  disableDelete
                />
              </CardContent>
            </Card>
          </div>
          {/* Thresholds */}
          <div className="w-full py-2">
            <Card variant="outlined">
              <CardContent>
                <div className="flex justify-between">
                  <b className="text-primary-600">{t('menu.thresholds')}</b>
                  <br />
                  <ButtonGroup className="px-2">
                    {!isDisabledEdit && (
                      <>
                        <Tooltip title={t(`${translationPrefix}.label.add-label`)}>
                          <Button variant="outlined" size="small" onClick={handleOpenLabelDialog}>
                            <TurnedInNotIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip title={t(`${translationPrefix}.label.add-threshold`)}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setOpenDialog(true)}
                            color="success">
                            <AddCircleOutlineIcon />
                          </Button>
                        </Tooltip>
                      </>
                    )}

                    <IsSuperAdmin>
                      <ConfirmationButtonDialog handleDelete={handleThresholdsBulkDelete} />
                    </IsSuperAdmin>
                  </ButtonGroup>
                </div>
                <EnhancedDataGrid
                  service={productWarehouseService}
                  columns={thresholdColumns}
                  refresh={thresholdsRefresh}
                  outerParams={{
                    warehouse_id: `${warehouse.id}`
                  }}
                />
              </CardContent>
            </Card>
            {/* Threshold Dialog */}
            <Dialog fullWidth maxWidth="lg" open={openDialog} onClose={handleClose}>
              <DialogTitle>{t(`${translationPrefix}.add-threshold`)}</DialogTitle>
              <DialogContent>
                <ProductWarehouseForm
                  products={products}
                  categories={categories}
                  statusOptions={statusOptions}
                  onSubmit={addThreshold}
                  labelOptions={labelOptions}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>{t('actions.cancel')}</Button>
                <Button type="submit" form="addProductThreshold">
                  {t('global.add')}
                </Button>
              </DialogActions>
            </Dialog>
            {/* Contract Line Dialog */}
            <Dialog
              fullWidth
              maxWidth="lg"
              open={openContractLineDialog}
              onClose={handleCloseContractDialog}>
              <DialogTitle>
                <div className="flex justify-between">
                  {t(`${translationPrefix}.contractline-list-menu`)}
                  {(!contractLines || contractLines.length === 0) && (
                    <ButtonGroup>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setOpenContractLineInsertDialog(true)}>
                        {t('actions.insert')}
                      </Button>
                    </ButtonGroup>
                  )}
                </div>
              </DialogTitle>
              <DialogContent>
                <DataGridPremium
                  density="compact"
                  autoHeight
                  rows={contractLines ?? []}
                  columns={contractLineColumns}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseContractDialog}>{t('actions.cancel')}</Button>
              </DialogActions>
            </Dialog>
            {/* Contract Line Insert Dialog */}
            <Dialog fullWidth maxWidth="sm" open={openContractLineInsertDialog}>
              <DialogTitle>{t(`${translationPrefix}.contractline-insert`)}</DialogTitle>
              <DialogContent>
                <WarehouseContractLineForm onSubmit={createContractLine} contracts={contracts} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseContractInsertDialog}>{t('actions.cancel')}</Button>
                <Button type="submit" form="addContractLine">
                  {t('global.add')}
                </Button>
              </DialogActions>
            </Dialog>
            {/* Label Threshold Dialog */}
            <Dialog fullWidth maxWidth="sm" open={openLabelDialog} onClose={handleCloseLabelDialog}>
              <DialogTitle>{t(`${translationPrefix}.label.add-title`)}</DialogTitle>
              <DialogContent>
                <LabelThresholdForm onSubmit={createLabelThreshold} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseLabelDialog}>{t('actions.cancel')}</Button>
                <Button type="submit" form="addLabelThreshold">
                  {t('global.add')}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
          {/* Locations */}
          {!hideTenantAdminSections && (
            <div className="w-full py-2">
              <Card variant="outlined">
                <CardContent>
                  <div className="flex justify-between">
                    <b className="text-primary-600">{t('menu.locations')}</b>
                    <br />
                    <ButtonGroup className="px-2">
                      <Button onClick={handleLocationDialog} variant="outlined" size="small">
                        <AddIcon />
                        {t(`${translationPrefix}.add-location`)}
                      </Button>
                    </ButtonGroup>
                  </div>
                  <EnhancedDataGrid
                    service={locationService}
                    columns={locationsColumns}
                    outerParams={{
                      warehouse_id: `${warehouse.id}`
                    }}
                    disableDelete={isDisabledEdit}
                  />
                </CardContent>
                <Dialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)}>
                  <DialogTitle>{t(`${translationPrefix}.add-location`)}</DialogTitle>
                  <DialogContent>
                    <LocationForm location={initLocation} onSubmit={createLocation} />
                  </DialogContent>
                </Dialog>
              </Card>
            </div>
          )}
          <div className="w-full md:w-1/2  p-4">
            <Card variant="outlined">
              <CardContent>
                <EntityHistory service={warehouseService} entityId={warehouse.id} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </TenantPage>
  );
}

export default EditWarehouse;
